import { ScheduleRule, CalendarEvent, WeekDay } from '../types';

// Convert schedule rules to calendar events
export function convertRulesToCalendarEvents(rules: ScheduleRule[], daysToGenerate?: number): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  
  // Safety check: limit the number of rules to process
  if (!rules || rules.length === 0) return [];
  if (rules.length > 100) {
    console.warn('Too many rules detected, limiting to first 100 for performance');
    rules = rules.slice(0, 100);
  }
  
  const today = new Date();
  
  // Generate events for a longer period to show recurring policies properly
  // Default to 90 days (about 3 months) or use provided value
  const maxDays = daysToGenerate || 90;
  
  for (let i = 0; i < maxDays; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    
    const dayOfWeek = getDayOfWeekFromDate(date);
    
    // Find rules that apply to this day
    const applicableRules = rules.filter(rule => 
      rule.isActive && rule.days && rule.days.includes(dayOfWeek)
    );
    
    // Limit the number of events per day to prevent overwhelming the UI
    const maxEventsPerDay = 5;
    const limitedRules = applicableRules.slice(0, maxEventsPerDay);
    
    // Create calendar events for each applicable rule
    limitedRules.forEach((rule, ruleIndex) => {
      // Safety check: ensure rule has required properties
      if (!rule.id || !rule.displayName || !rule.startTime) {
        console.warn('Skipping invalid rule:', rule);
        return;
      }
      
      const event: CalendarEvent = {
        id: `${rule.id}-${date.toISOString().split('T')[0]}-${ruleIndex}`,
        title: `Block ${rule.displayName}`,
        description: `Blocking ${rule.site || 'scheduled content'} during scheduled hours`,
        date: date.toISOString().split('T')[0],
        time: rule.startTime,
        endTime: rule.endTime, // Add end time to the event
        duration: calculateDuration(rule.startTime, rule.endTime),
        type: 'block',
        color: rule.color || getCategoryColor(rule.category),
        ruleId: rule.id
      };
      
      events.push(event);
    });
    
    // Safety check: limit total events to prevent memory issues
    if (events.length >= 500) {
      console.warn('Reached maximum event limit, stopping generation');
      break;
    }
  }
  
  return events;
}

// Convert Date object to WeekDay type
function getDayOfWeekFromDate(date: Date): WeekDay {
  const days: WeekDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
}

// Calculate duration between start and end time
function calculateDuration(startTime: string, endTime: string): string {
  try {
    if (!startTime || !endTime) return 'All day';
    
    const start = new Date(`2000-01-01T${startTime}:00`);
    const end = new Date(`2000-01-01T${endTime}:00`);
    
    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'All day';
    }
    
    const diffMs = end.getTime() - start.getTime();
    
    // Handle cases where end time is before start time (overnight)
    if (diffMs < 0) {
      const adjustedDiffMs = diffMs + (24 * 60 * 60 * 1000); // Add 24 hours
      const diffHours = Math.floor(adjustedDiffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((adjustedDiffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours === 0) {
        return `${diffMinutes} minutes`;
      } else if (diffMinutes === 0) {
        return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`;
      } else {
        return `${diffHours}h ${diffMinutes}m`;
      }
    }
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours === 0) {
      return `${diffMinutes} minutes`;
    } else if (diffMinutes === 0) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`;
    } else {
      return `${diffHours}h ${diffMinutes}m`;
    }
  } catch (error) {
    console.error('Error calculating duration:', error);
    return 'All day';
  }
}

// Get color based on category
function getCategoryColor(category: string): string {
  const colors = {
    social: '#00F5A0',
    entertainment: '#8B5CF6',
    news: '#0AEFFF',
    shopping: '#00F5A0',
    productivity: '#0AEFFF',
    custom: '#8B5CF6'
  };
  return colors[category as keyof typeof colors] || '#8B5CF6';
}

// Group events by date for easier calendar rendering
export function groupEventsByDate(events: CalendarEvent[]): Record<string, CalendarEvent[]> {
  if (!events || events.length === 0) return {};
  
  return events.reduce((acc, event) => {
    if (!acc[event.date]) {
      acc[event.date] = [];
    }
    acc[event.date].push(event);
    return acc;
  }, {} as Record<string, CalendarEvent[]>);
}

// Get events for a specific date
export function getEventsForDate(events: CalendarEvent[], date: string): CalendarEvent[] {
  if (!events || events.length === 0) return [];
  return events.filter(event => event.date === date);
}

// Check if a rule is active on a specific date
export function isRuleActiveOnDate(rule: ScheduleRule, date: Date): boolean {
  if (!rule.isActive || !rule.days || !Array.isArray(rule.days)) return false;
  
  const dayOfWeek = getDayOfWeekFromDate(date);
  return rule.days.includes(dayOfWeek);
}

// Get all active rules for a specific date
export function getActiveRulesForDate(rules: ScheduleRule[], date: Date): ScheduleRule[] {
  if (!rules || rules.length === 0) return [];
  return rules.filter(rule => isRuleActiveOnDate(rule, date));
}
